import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BlockIcon from "@mui/icons-material/Block";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { DataGridPro, GridToolbar, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useIntl } from "react-intl";
import { tokens } from "../../../assets/theme";
import { hasAnyRole } from "../../../auth/authUi";
import { useAuth } from "../../../auth/AuthContext";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import { formatQuantity } from "../../cmms/cmmsFormat";
import { getAssetHierarchyPalette, getDataGridPanelSx, getStatusChipSx } from "../../cmms/assetHierarchyStyles";
import { applyColumnWidths, readStoredJson, reorderColumns } from "../../cmms/cmmsGridPrefs";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import messages from "../messages";

const columnOrderKey = "cmmsSpareParts-columnOrder";
const columnWidthsKey = "cmmsSpareParts-columnWidths";
// The third of the same kind: order and width have been remembered here from the start,
// and which columns you want is the same sort of preference as how wide you want them.
const columnVisibilityKey = "cmmsSpareParts-columnVisibility";

/*
 * The columns hidden unless someone asks for them.
 *
 * The supplier's article number and the second name line are reference data: you look one up when
 * you are checking a delivery note, not every time you open the register. Showing them by default
 * would put nine text columns on a grid that already needs seven, and the Columns panel in the
 * toolbar is the control that turns them on.
 *
 * The type joined the list with FE-15.15, on the same argument in a different shape: it is what a
 * part *is*, decided once when the part is created and rarely looked at again. What you read off
 * the register is the balance. The two flattened lookups side by side would also have cost the
 * name column its width on a narrow screen, and the name is what people scan.
 *
 * Min and max are reference data of the same kind, and joined that list with FE-15.13. They are
 * settings somebody decides once per part, not numbers anyone reads off the register — what you
 * read off the register is the balance. Min was visible before that story and is not now, which
 * is the one column here that used to show and no longer does.
 *
 * Nothing about the reorder alarm depends on that. The on-hand cell already renders bold and in
 * warning.main when the server says isBelowReorderPoint, and the tile at the top of the page
 * counts them; hiding min hides the level, never the fact that a part is under it. Someone who
 * wants the number itself turns the column on, and the choice is remembered.
 *
 * Searching them is not gated on any of this. sparePartSearch.js reads the fields off the row, so
 * a hidden column is still a findable one.
 *
 * An erpArticleNumber key survives in the saved model of anyone who opened the Columns panel
 * before that column was dropped. Harmless — the merge below spreads it over nothing, and the grid
 * has no such field to apply it to.
 */
const defaultColumnVisibility = {
  supplierArticleNumber: false,
  name2: false,
  sparePartTypeName: false,
  reorderPoint: false,
  maxStockLevel: false,
};

const initFromStorage = (cols) => {
  let out = cols;
  const order = readStoredJson(columnOrderKey, null);
  if (order) out = reorderColumns(order, out);
  const widths = readStoredJson(columnWidthsKey, null);
  if (widths) out = applyColumnWidths(widths, out);
  return out;
};

/*
 * The spare parts register grid.
 *
 * The column set comes from the cmms/poc grid, cut against our view model: its
 * totalQuantityOnHand and reorderPoint are exactly the two fields the server derives. Its
 * manufacturer and category columns are gone with the fields behind them (§3.5), and its
 * hard-coded `$${value.toFixed(2)}` is gone with the assumption that everyone bills in dollars.
 *
 * Everything else — storage, skeleton, empty state — follows RecurringSchedulesDataGrid.
 */
export default function SparePartsDataGrid({
  spareParts,
  isLoading,
  showStatusColumn,
  onEdit,
  onOpenStock,
  onRetire,
  onRestore,
  emptyState,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const apiRef = useGridApiRef();
  const { roleIds } = useAuth();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;
  // Lazy initialiser so the read happens once rather than on every render. Read the same way the
  // two sibling keys are, without a guard: three keys that behave alike beat one of them being
  // defensively different.
  // The saved model wins where it has an opinion, and defaultColumnVisibility fills the rest. Plain
  // `saved ?? defaults` would show all three to anyone who had already touched the panel once,
  // because their saved model predates the columns and says nothing about them.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    return { ...defaultColumnVisibility, ...readStoredJson(columnVisibilityKey, null) };
  });
  const [sortModel, setSortModel] = useState([{ field: "partNumber", sort: "asc" }]);

  const canManage = hasAnyRole(roleIds, CMMS_MANAGER_ROLES);

  const baseColumns = useMemo(() => {
    const columns = [
      {
        field: "partNumber",
        headerName: intl.formatMessage(messages.columnPartNumber),
        flex: 0.4,
        minWidth: 130,
      },
      // The supplier's number, beside the one it is not. Hidden by default; see
      // defaultColumnVisibility above.
      {
        field: "supplierArticleNumber",
        headerName: intl.formatMessage(messages.columnSupplierArticleNumber),
        flex: 0.4,
        minWidth: 150,
        valueGetter: (params) => params.row.supplierArticleNumber ?? "",
      },
      { field: "name", headerName: intl.formatMessage(messages.columnName), flex: 1, minWidth: 200 },
      {
        field: "name2",
        headerName: intl.formatMessage(messages.columnName2),
        flex: 0.6,
        minWidth: 160,
        valueGetter: (params) => params.row.name2 ?? "",
      },
      // The two names the server flattened off their foreign keys, together because they are the
      // same kind of column. Both are field-named after the property that is actually on the row
      // rather than after the navigation behind it: a field name that matches nothing on the row
      // is the kind of thing that reads as correct for a year.
      {
        field: "sparePartTypeName",
        headerName: intl.formatMessage(messages.columnSparePartType),
        flex: 0.4,
        minWidth: 120,
        valueGetter: (params) => params.row.sparePartTypeName ?? "",
      },
      {
        field: "storageLocationName",
        headerName: intl.formatMessage(messages.columnStorageLocation),
        flex: 0.4,
        minWidth: 120,
        valueGetter: (params) => params.row.storageLocationName ?? "",
      },
      { field: "unit", headerName: intl.formatMessage(messages.columnUnit), width: 90 },
      {
        field: "unitCost",
        headerName: intl.formatMessage(messages.columnUnitCost),
        width: 120,
        type: "number",
        // No currency symbol. The register records a number; which currency it is in is a
        // company setting this module does not have, and guessing one is worse than omitting it.
        valueFormatter: (params) =>
          (params.value === null || params.value === undefined ? "" : formatQuantity(intl, params.value, 2)),
      },
      {
        field: "quantityOnHand",
        headerName: intl.formatMessage(messages.columnQuantityOnHand),
        width: 120,
        type: "number",
        // The flag is the server's answer, never recomputed here. The reorder rule is `<=` and
        // it lives in one place; a second comparison in the grid is a second place to get it
        // wrong, and it would be the one the user actually sees.
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontWeight: params.row.isBelowReorderPoint ? 700 : 400, color: params.row.isBelowReorderPoint ? "warning.main" : "inherit", fontSize: "inherit" }}
          >
            {formatQuantity(intl, params.row.quantityOnHand, 2)}
          </Typography>
        ),
      },
      // The level pair: order at min, order up to max. Both hidden by default — see
      // defaultColumnVisibility above.
      {
        field: "reorderPoint",
        headerName: intl.formatMessage(messages.columnReorderPoint),
        width: 130,
        type: "number",
        // Blank, not a zero. No level set is not a level of nought, and a zero here would read
        // as a part nobody ever wants to reorder.
        valueFormatter: (params) =>
          (params.value === null || params.value === undefined ? "" : formatQuantity(intl, params.value, 2)),
      },
      {
        field: "maxStockLevel",
        headerName: intl.formatMessage(messages.columnMaxStockLevel),
        width: 130,
        type: "number",
        // Same rule, same reason: a blank ceiling is no ceiling, and a zero would read as a part
        // nobody is ever allowed to stock.
        valueFormatter: (params) =>
          (params.value === null || params.value === undefined ? "" : formatQuantity(intl, params.value, 2)),
      },
    ];

    if (showStatusColumn) {
      columns.push({
        field: "isActive",
        headerName: intl.formatMessage(messages.columnActive),
        width: 110,
        sortable: false,
        // Kept out of the Columns panel. This column already has a user control: it
        // only exists while "Show inactive" is on. A second control could hide the chip and
        // leave that switch revealing inactive rows with no way to tell which they are.
        hideable: false,
        renderCell: (params) => (
          <Chip
            size="small"
            label={intl.formatMessage(params.row.isActive ? messages.statusActive : messages.statusRetired)}
            sx={getStatusChipSx(cmmsColors, params.row.isActive)}
          />
        ),
      });
    }

    columns.push({
      field: "actions",
      headerName: "",
      width: canManage ? 140 : 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // Kept out of the Columns panel. With no headerName it would list as a blank
      // unlabelled switch, and switching it off would quietly remove the row's only actions.
      hideable: false,
      renderCell: (params) => (
        <Box onClick={(event) => event.stopPropagation()}>
          {canManage &&
            <Tooltip title={intl.formatMessage(messages.actionEdit)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.actionEdit)}
                onClick={() => onEdit?.(params.row)}
                sx={{ backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {/* No role gate: reading a part's movements is Cmms_Field. */}
          <Tooltip title={intl.formatMessage(messages.actionStock)}>
            <IconButton
              size="small"
              aria-label={intl.formatMessage(messages.actionStock)}
              onClick={() => onOpenStock?.(params.row)}
              sx={{ ml: canManage ? 1 : 0, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
            >
              <Inventory2Icon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          {/*
            * Retire and its opposite share the slot. A retired row is only on screen
            * with "Show retired" on, and when it is, the one thing a manager can still do to it
            * is bring it back. Leaving the slot empty there is what made a mis-click permanent.
            */}
          {canManage && params.row.isActive &&
            <Tooltip title={intl.formatMessage(messages.actionRetire)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.actionRetire)}
                onClick={() => onRetire?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <BlockIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {canManage && !params.row.isActive &&
            <Tooltip title={intl.formatMessage(messages.actionRestore)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.actionRestore)}
                onClick={() => onRestore?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <RestoreFromTrashIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
        </Box>
      ),
    });

    return columns;
  }, [intl, colors, cmmsColors, canManage, showStatusColumn, onEdit, onOpenStock, onRetire, onRestore]);

  const columns = useMemo(() => initFromStorage(baseColumns), [baseColumns]);

  const gridSlots = useMemo(
    () => ({
      // Columns, Filters, Density and Export — the same toolbar features/home, oeegrid, material
      // and productionPlan mount. Its labels come from the MUI locale already passed below, so
      // this adds no strings of our own.
      toolbar: GridToolbar,
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={8} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the
      // grid with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  const handleColumnOrderChange = () => {
    if (!apiRef.current) return;
    // Every column, not just the visible ones: a hidden column left out here is a hidden column
    // missing from the saved order, and it comes back at the far right once it is unhidden.
    const fields = apiRef.current.getAllColumns().map((c) => c.field);
    localStorage.setItem(columnOrderKey, JSON.stringify(fields));
  };

  const handleColumnResize = (params) => {
    const map = readStoredJson(columnWidthsKey, {});
    map[params.colDef.field] = params.width;
    localStorage.setItem(columnWidthsKey, JSON.stringify(map));
  };

  const handleColumnVisibilityModelChange = (model) => {
    setColumnVisibilityModel(model);
    localStorage.setItem(columnVisibilityKey, JSON.stringify(model));
  };

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
          // The toolbar's buttons default to the theme's primary, which here is the surface
          // colour — dark lettering on a dark bar. gray[100] is the foreground token and inverts
          // with the mode, so this reads in both. The same three lines the other 28 grids in the
          // app carry; GridToolbar was mounted here without them.
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.gray[100]} !important`,
          },
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={spareParts || []}
          getRowId={(row) => row.id}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnResize={handleColumnResize}
          slotProps={{ panel: { sx: getDataGridPanelSx(theme.palette.mode) } }}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
